import { supabase } from '@/integrations/supabase/client';

export interface BlogPost {
    id: string;
    slug: string;
    title: string;
    subtitle: string | null;
    excerpt: string;
    content: string;
    cover_image: string | null;
    tags: string[];
    category: string;
    author: string;
    published_at: string | null;
    status: string;
    reading_time: number;
    created_at: string;
    updated_at: string;
}

/** Il post senza il corpo: basta per liste, card e link correlati. */
export type BlogPostSummary = Omit<BlogPost, 'content'>;

const SUMMARY_COLUMNS =
    'id,slug,title,subtitle,excerpt,cover_image,tags,category,author,published_at,status,reading_time,created_at,updated_at';

export const blogApi = {
    async getPosts(options?: {
        tag?: string;
        category?: string;
        search?: string;
        limit?: number;
        offset?: number;
    }): Promise<BlogPost[]> {
        let query = supabase
            .from('blog_posts')
            .select('*')
            .eq('status', 'published')
            .order('published_at', { ascending: false });

        if (options?.tag) {
            query = query.contains('tags', [options.tag]);
        }
        if (options?.category) {
            query = query.eq('category', options.category);
        }
        if (options?.search) {
            query = query.or(`title.ilike.%${options.search}%,excerpt.ilike.%${options.search}%`);
        }
        if (options?.limit) {
            query = query.limit(options.limit);
        }
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options?.limit || 10) - 1);
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data || []) as BlogPost[];
    },

    /** Tutti i post pubblicati, senza corpo. Stessa forma di src/data/blog-index.json. */
    async getPostSummaries(): Promise<BlogPostSummary[]> {
        const { data, error } = await supabase
            .from('blog_posts')
            .select(SUMMARY_COLUMNS)
            .eq('status', 'published')
            .order('published_at', { ascending: false });

        if (error) throw error;
        return (data || []) as unknown as BlogPostSummary[];
    },

    async getPostBySlug(slug: string): Promise<BlogPost | null> {
        const { data, error } = await supabase
            .from('blog_posts')
            .select('*')
            .eq('slug', slug)
            .eq('status', 'published')
            .maybeSingle();

        if (error) throw error;
        return data as BlogPost | null;
    },
};
